export type Papel = 'admin' | 'financeiro';
export type StatusUsuario = 'pendente' | 'aprovado' | 'rejeitado' | 'revogado';
export type StatusInscricao = 'draft' | 'submitted' | 'cancelled';
export type TipoPagamento = 'pix' | 'cartao_credito' | 'cartao_debito' | 'dinheiro';

export type Usuario = {
  id: string;
  nome: string;
  email: string;
  papel: Papel | null;
  status: StatusUsuario;
  aprovadoPor: string | null;
  aprovadoEm: string | null;
  motivoRejeicao: string | null;
  criadoEm: string;
};

/** O que a API pública devolve ao responsável durante o preenchimento. */
export type InscricaoRascunho = {
  token: string;
  protocolo: string;
  status: StatusInscricao;
  step: number;
  email: string;
  nomeAdolescente: string;
  nomeResponsavel: string | null;
  telefoneResponsavel: string | null;
  telefoneAdolescente: string | null;
  dados: Record<string, unknown>;
  /** null quando a retomada foi por chave — saúde é repreenchida (seção 5) */
  saude: Record<string, unknown> | null;
  house: string | null;
  pedidoQuarto: string | null;
  valorTotal: string;
  atualizadoEm: string;
};

export type RespostaIniciar = {
  retomada: boolean;
  saudeOmitida?: boolean;
  aviso?: string;
  inscricao: InscricaoRascunho;
};

export type LinhaInscricao = {
  id: string;
  protocolo: string;
  status: StatusInscricao;
  nomeAdolescente: string;
  nomeResponsavel: string | null;
  email: string;
  telefoneResponsavel: string | null;
  house: string | null;
  valorTotal: string;
  temSaude: boolean;
  autorizacaoAssinadaEm: string | null;
  enviadaEm: string | null;
  atualizadoEm: string;
};

export type FichaCompleta = Omit<InscricaoRascunho, 'token'> & {
  id: string;
  nomeNormalizado: string;
  autorizacaoAssinadaEm: string | null;
  enviadaEm: string | null;
  canceladaEm: string | null;
  criadoEm: string;
};

export type Saldo = {
  valorTotal: string;
  pago: string;
  saldo: string;
  quitado: boolean;
};

export type Lancamento = {
  id: string;
  natureza: 'pagamento' | 'estorno';
  tipo: TipoPagamento;
  valor: string;
  estornoDe: string | null;
  observacao: string | null;
  pagoEm: string;
  lancadoPor: string;
  criadoEm: string;
};

/**
 * A projeção do painel financeiro. Note a ausência de `nomeAdolescente`,
 * `dados` e `saude`: o backend nunca envia esses campos por esta rota, e o
 * tipo reflete isso para o compilador cobrar (seção 10.4).
 */
export type LinhaFinanceira = {
  id: string;
  protocolo: string;
  status: StatusInscricao;
  nomeAdolescente: string;
  nomeResponsavel: string | null;
  emailResponsavel: string;
  telefoneResponsavel: string | null;
  valorTotal: string;
  enviadaEm: string | null;
  pago: string;
  saldo: string;
  quitado: boolean;
};

/**
 * previsto/recebido/aReceber só vêm para `admin` — o financeiro trabalha
 * inscrição por inscrição e a API omite os totais do acampamento para ele.
 */
export type ResumoFinanceiro = {
  inscricoes: number;
  previsto?: string;
  recebido?: string;
  aReceber?: string;
};

export type RegrasEnvio = {
  confirmacao: boolean;
  autorizacao: boolean;
  lembretePagamento: boolean;
  avisoSaudeFaltando: boolean;
  lembreteEmbarque: boolean;
};

export type Configuracao = {
  id: string;
  formOpen: boolean;
  closedMessage: string;
  chaveExcecao: string | null;
  valorPadrao: string;
  dataAcampamento: string | null;
  local: string;
  periodo: string;
  instrucoesPagamento: string;
  regrasEnvio: RegrasEnvio;
  atualizadoEm: string;
  atualizadoPor: string | null;
};

export type ConfigPublica = {
  formOpen: boolean;
  closedMessage: string;
  valorPadrao: string;
  dataAcampamento: string | null;
  local: string;
  periodo: string;
  instrucoesPagamento: string;
};

export type RegistroAudit = {
  id: string;
  actorType: 'admin' | 'financeiro' | 'guardian' | 'system';
  actorId: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  payload: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  criadoEm: string;
  /** Só vem preenchido quando actorType é admin/financeiro — ver o comentário
   * na rota /admin/audit sobre o que actorId significa em cada caso. */
  atorNome: string | null;
  atorEmail: string | null;
};

export const ROTULO_PAGAMENTO: Record<TipoPagamento, string> = {
  pix: 'Pix',
  cartao_credito: 'Cartão de crédito',
  cartao_debito: 'Cartão de débito',
  dinheiro: 'Dinheiro',
};

export const ROTULO_PAPEL: Record<Papel, string> = {
  admin: 'Administrador',
  financeiro: 'Financeiro',
};
